/**
 * Authentication utilities for Zero schema.
 *
 * Contains the AuthContext type and permission check helpers.
 */

/**
 * Authentication context passed to queries and mutators.
 * Populated by your server from the authenticated session.
 *
 * Role hierarchy (must match frontend-features/rbac/permissions.ts):
 * - admin: Full access
 * - moderator: Limited admin access
 * - user: Standard access
 * - null: Unauthenticated
 */
export interface AuthContext {
  userID: string | null;
  role: "admin" | "moderator" | "user" | null;
}

/**
 * Throws if user is not an admin.
 * On client: skipped (optimistic update proceeds)
 * On server: enforced (mutation rejected if unauthorized)
 */
export function requireAdmin(ctx: AuthContext): void {
  if (ctx.role !== "admin") {
    throw new Error("Admin access required");
  }
}

/**
 * Throws if user is not at least a moderator.
 */
export function requireModerator(ctx: AuthContext): void {
  if (ctx.role !== "admin" && ctx.role !== "moderator") {
    throw new Error("Moderator access required");
  }
}

/**
 * Throws if user is not authenticated.
 */
export function requireAuthenticated(ctx: AuthContext): void {
  if (!ctx.userID) {
    throw new Error("Authentication required");
  }
}
